import { Component, Input, OnChanges, OnInit , CUSTOM_ELEMENTS_SCHEMA} from '@angular/core';
import SearchItem from '@Interfaces/SearchItem';
import { SearchActions } from '@app/core/ngrx/actions';
import { State } from '@app/core/ngrx/reducers';
import { searchSelectors } from '@app/core/ngrx/selectors';
import { ToolService } from '@app/core/services/tool/tool.service';
import { faFilter, faSort, faSortDown, faSortNumericDown, faSortNumericUp, faSortUp } from '@fortawesome/pro-regular-svg-icons';
import { faTags } from '@fortawesome/pro-solid-svg-icons';
import { PopoverController } from '@ionic/angular';
import { select, Store } from '@ngrx/store';
import { fromEvent } from 'rxjs';
import { ProgramSearchFiltersListComponent } from '../program-search-filters-list/program-search-filters-list.component';
import { ProgramService } from '@app/core/services/program/program.service';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { QueryService } from '@app/core/graphql/query/query.service';
import { userSelectors } from '@app/core/ngrx/selectors';
import { ActivatedRoute } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { RouterModule } from '@angular/router';

@Component({
    selector: 'app-search-filters',
    templateUrl: './search-filters.component.html',
    styleUrls: ['./search-filters.component.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, IonicModule, FontAwesomeModule, RouterModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]})
export class SearchFiltersComponent implements OnChanges {
    /**
     * @author Steve Ford
     * @description
     *    pass in the whole list of options each filter button uses when clicked
     *
     * after dismissing the popovers use the list's checked = true values as search parameters
     */

    customAlertOptions: any = {
        cssClass: 'sortby-selection',
    };

    faFilter = faFilter;
    faSort = faSort;
    faSortDown = faSortDown;
    faSortNumericDown = faSortNumericDown;
    faSortNumericUp = faSortNumericUp;
    faSortUp = faSortUp;
    showMap = false;
    orderStart = true;

    sortUp$ = this.toolService.getSearchSort();
    programCount$ = this.toolService.getSearchProgramCount();

    programBundleMapDisabled$ = this.store.select(searchSelectors.getProgramBundleMapDisabled);

    programBundleRowActivated$ = this.store.select(searchSelectors.getProgramBundle);

    label$ = this.toolService.getSearchSortLabel();

    cities: any;
    states: any;

    sortOrder: string = 'price-asc';

    subscription = this.toolService.getShowMap();

    context$ = this.store.select(searchSelectors.getSearchContext);

    showSuggested$ = this.store.select(searchSelectors.getShowSuggested);

    showProgramBundle$ = this.store.select(searchSelectors.getProgramBundle);

    @Input() data: any;
    @Input() search: any;
    @Input() alreadyShowMap?: boolean;

    constructor(
        private store: Store<State>,
        private toolService: ToolService,
        private queryService: QueryService,
        private programService: ProgramService,
        private popoverController: PopoverController,
        private angulartics2GoogleTagManager: Angulartics2GoogleTagManager,
        private route: ActivatedRoute
    ) {
        this.route.queryParams.subscribe((queryParams) => {
            if (queryParams.order) {
                this.sortOrder = queryParams.order;
            }
        });

        this.watchWindow();
    }

    ngOnChanges() {
        if (this.alreadyShowMap) {
            this.showMap = true;
        }
    }

    watchWindow() {
        fromEvent(window, 'resize').subscribe((response: any) => {
            if (response.target.outerWidth < 600) {
                this.toolService.showMap.next(false);
                this.showMap = false;
            } else {
                if (this.showMap) {
                    this.toolService.showMap.next(true);
                }
            }
        });
    }

    async presentPopover(ev: any, item: string) {
        let itemList = [];
        if (this.data) {
            try {
                if (item === 'specialty') {
                    this.angulartics2GoogleTagManager.eventTrack('Filter Specialty', {
                        category: 'Search',
                    });
                    if (this.data.specialty) {
                        itemList = this.data.specialty.map((specialty: any) => specialty);
                    }
                } else if (item === 'states') {
                    this.angulartics2GoogleTagManager.eventTrack('Filter States', {
                        category: 'Search',
                    });
                    if (this.data.states) {
                        this.data.cities.forEach((val: SearchItem) => ({ ...val, checked: false }));

                        itemList = this.data.states.map((location: any) => location);
                    }
                } else if (item === 'cities') {
                    this.angulartics2GoogleTagManager.eventTrack('Filter Cities', {
                        category: 'Search',
                    });

                    if (this.data.states) {
                        this.data.states.forEach((val: SearchItem) => ({ ...val, checked: false }));
                    }

                    if (this.data.cities) {
                        itemList = this.data.cities.map((location: any) => location);
                    }
                } else if (item === 'locations') {
                    this.angulartics2GoogleTagManager.eventTrack('Filter Locations', {
                        category: 'Search',
                    });
                    if (this.data.locations) {
                        itemList = this.data.locations.map((location: any) => location);
                    }
                } else if (item === 'tags') {
                    this.angulartics2GoogleTagManager.eventTrack('Filter Tags', {
                        category: 'Search',
                    });
                    if (this.data.tags) {
                        itemList = this.data.tags.map((tag: any) => tag);
                    }
                } else if (item === 'types') {
                    this.angulartics2GoogleTagManager.eventTrack('Filter Types', {
                        category: 'Search',
                    });
                    if (this.data.types) {
                        itemList = this.data.types.map((type: any) => type).sort((a, b) => a.value.localeCompare(b.value));
                    }
                } else if (item === 'dates') {
                    if (this.data.dates) {
                        itemList = this.data.dates.map((date: any) => date);
                    }
                }
            } catch (error) {
                console.log('Error in presentPopover', error);
            }
        } else {
            console.log('No data found');
        }

        const popover = await this.popoverController.create({
            component: ProgramSearchFiltersListComponent,
            cssClass: 'filter-popup',
            componentProps: {
                list: itemList,
            },
            event: ev,
            translucent: true,
        });
        popover.present();

        const response = await popover.onDidDismiss();

        if (response && response.data) {
            // reset categories when clicking a filter
            this.programService.setResetCatagoriesSubject.next(true); // check for filled category buttons
            // set search page as loading
            this.store.dispatch(SearchActions.setSearchLoading({ loading: true }));

            // filter the copy of search categories to get the checked values
            let specialties =
                item === 'specialty' && response.data.clear
                    ? []
                    : [...this.data.specialty]
                          .filter((val: SearchItem) => response.data.value === val.value)
                          .map(() => {
                              return { ...response.data };
                          })
                          .concat(...this.data.specialty.filter((val: SearchItem) => val.value !== response.data.value && val.checked === true));

            let locations =
                item === 'locations' && response.data.clear
                    ? []
                    : [...this.data.locations]
                          .filter((val: SearchItem) => response.data.value === val.value)
                          .map(() => {
                              return { ...response.data };
                          })
                          .concat(...this.data.locations.filter((val: SearchItem) => val.value !== response.data.value && val.checked === true));

            let types =
                item === 'types' && response.data.clear
                    ? []
                    : [...this.data.types]
                          .filter((val: SearchItem) => response.data.value === val.value)
                          .map(() => {
                              return { ...response.data };
                          })
                          .concat(...this.data.types.filter((val: SearchItem) => val.value !== response.data.value && val.checked === true));

            let tags =
                item === 'tags' && response.data.clear
                    ? []
                    : [...this.data.tags]
                          .filter((tag: SearchItem) => response.data.value === tag.value)
                          .map(() => {
                              return { ...response.data };
                          })
                          .concat(...this.data.tags.filter((tag: SearchItem) => tag.value !== response.data.value && tag.checked === true));

            let cities =
                item === 'cities' && response.data.clear
                    ? []
                    : [...this.data.cities]
                          .filter((val: SearchItem) => response.data.value === val.value)
                          .map(() => {
                              return { ...response.data };
                          })
                          .concat(...this.data.cities.filter((val: SearchItem) => val.value !== response.data.value && val.checked === true));

            let states =
                item === 'states' && response.data.clear
                    ? []
                    : [...this.data.states]
                          .filter((val: SearchItem) => response.data.value === val.value)
                          .map(() => {
                              return { ...response.data };
                          })
                          .concat(...this.data.states.filter((val: SearchItem) => val.value !== response.data.value && val.checked === true));

            let dates =
                item === 'dates' && response.data.clear
                    ? []
                    : [...this.data.dates]
                          .filter((val: SearchItem) => response.data.value === val.value)
                          .map(() => {
                              return { ...response.data };
                          })
                          .concat(...this.data.dates.filter((val: SearchItem) => val.value !== response.data.value && val.checked === true));

            // share selections with the search page module
            const data = {
                specialty: specialties,
                locations,
                types,
                tags,
                cities,
                states,
                dates,
            };
            this.toolService.shareFilters.next(data);
        }
    }

    toggleMap(ev: any) {
        // toggle map on program search page
        this.angulartics2GoogleTagManager.eventTrack('Toggle Map ' + this.showMap, { category: 'Search' });

        if (!this.alreadyShowMap) {
            // get map programs
            this.queryService
                .programSearch({
                    limit: 500,
                    offset: 0,
                    order: this.sortOrder,
                })
                .toPromise()
                .then((searchResponse: any) => {
                    // enable map component
                    this.toolService.showMap.next(this.showMap);
                });
        } else {
            this.toolService.showMap.next(this.showMap);
        }
    } // end toggle map

    sortBy(ev: any) {
        this.angulartics2GoogleTagManager.eventTrack('Sort By ' + ev.detail.value, { category: 'Search' });

        this.toolService.searchSortValue.next(ev.detail.value);
    }

    reset() {
        this.angulartics2GoogleTagManager.eventTrack('Clear All Filters Button Click', { category: 'Search' });

        this.store.dispatch(SearchActions.setSearchLoading({ loading: true }));

        this.store.dispatch(
            SearchActions.setSearchContext({
                context: 'Search for your next clinical experience with us.',
            })
        );

        // hack to reset all filters
        const data = {
            specialty: [],
            types: [],
            tags: [],
            cities: [],
            states: [],
            locations: [],
            dates: [],
        };

        this.programService.setResetCatagoriesSubject.next(true); // check for filled category buttons

        this.toolService.shareFilters.next(data);

        this.store.dispatch(
            SearchActions.setProgramBundleMapDisabled({
                programBundleMapDisabled: false,
            })
        );

        // this.toolService.clearFilters.next(true);
    }
}
